/**
 * @fileoverview Voucher redemption landing — the page customers land on
 * after clicking an emailed gift link (`/redeem/<code>`).
 *
 * Flow:
 *   1. Public lookup of the voucher by code → display offer details.
 *   2. If not signed in, surface login + register buttons that return here.
 *   3. Once authed, claim the voucher (binds it to the user's account).
 *   4. Tell the user "Got it — now scan a wash bay QR to redeem."
 *
 * The Pay screen will surface this voucher as a discount option whenever the
 * customer hits a bay that matches the voucher's location/bay-type/tier scope.
 */

import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { api, ApiError } from '../../src/api/client';
import { useAuth } from '../../src/context/AuthContext';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, type ColorTheme } from '../../src/utils/theme';

interface VoucherLookup {
  code: string;
  kind: 'gift' | 'promo' | 'points_redemption';
  discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit';
  percentOff: number | null;
  tierName: string | null;
  bayType: 'self-serve' | 'automatic' | null;
  freeMinutes: number | null;
  /** Wash-credit dollars deposited at claim. NOT returned by by-code today —
   *  filled in from the claim response's full voucher row when available. */
  creditCents?: number | null;
  /** Server-rendered scope incl. Vacuums/Vending; null on legacy rows. */
  appliesToLabel?: string | null;
  status: 'pending' | 'active' | 'redeemed' | 'expired' | 'revoked';
  expiresAt: string | null;
  message: string | null;
  ownerName: string;
  locationName: string | null;
  locationId: string | null;
  redemptionsRemaining: number;
}

function offerHeadline(v: VoucherLookup): string {
  if (v.discountKind === 'credit') {
    // by-code omits creditCents; the claim response fills it in.
    return v.creditCents != null ? `$${(v.creditCents / 100).toFixed(2)} gift credit` : 'Gift credit';
  }
  if (v.discountKind === 'free_vend') {
    // by-code omits freeVendCents; the vend screen shows the exact amount.
    return 'Free vending credit';
  }
  if (v.discountKind === 'free_wash') {
    return v.tierName ? `Free ${v.tierName} wash` : 'Free wash';
  }
  if (v.discountKind === 'free_minutes') {
    return `${v.freeMinutes} free minutes`;
  }
  return v.percentOff === 100
    ? (v.tierName ? `Free ${v.tierName} wash` : 'Free wash')
    : `${v.percentOff}% off${v.tierName ? ` a ${v.tierName} wash` : ''}`;
}

/**
 * Hard-terminal statuses only. 'redeemed' is NOT terminal here: a fully-claimed
 * QR still belongs to its claim-holders, so the claim call must run (it returns
 * alreadyClaimed for holders and VOUCHER_EXHAUSTED for everyone else).
 */
function statusBanner(status: VoucherLookup['status']): { text: string; tone: 'ok' | 'warn' | 'err' } | null {
  if (status === 'expired') return { text: 'This gift has expired', tone: 'err' };
  if (status === 'revoked') return { text: 'This gift was revoked by the sender', tone: 'err' };
  return null;
}

export default function RedeemVoucherScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const { code } = useLocalSearchParams<{ code?: string }>();
  const { isAuthenticated, isLoading: authLoading, role } = useAuth();
  /** Role-appropriate landing — owners/admins shouldn't be dumped on /user. */
  const homePath = role === 'owner' ? '/owner' : role === 'admin' ? '/admin' : '/user';
  /** Where the claimed gift lives: customers' Rewards tab, owners' scan page. */
  const rewardsPath = role === 'owner' ? '/owner/scan' : role === 'admin' ? '/admin' : '/user/rewards';
  const goToRewards = React.useCallback(() => router.replace(rewardsPath as never), [router, rewardsPath]);

  const [voucher, setVoucher] = useState<VoucherLookup | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);

  // Initial public lookup
  useEffect(() => {
    if (!code) {
      setError('Missing redemption code');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const v = await api<VoucherLookup>(`/api/vouchers/by-code/${encodeURIComponent(code)}`, { skipAuth: true });
        if (!cancelled) setVoucher(v);
      } catch (e: any) {
        if (!cancelled) setError(e instanceof ApiError ? (e.message || 'Voucher not found') : 'Voucher not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  // Once authed, automatically claim the voucher
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!voucher) return;
    if (claimed || claiming) return;
    if (statusBanner(voucher.status)) return; // already terminal

    setClaiming(true);
    (async () => {
      try {
        const res = await api<{ ok: boolean; alreadyClaimed?: boolean; voucher?: { creditCents?: number | null } }>(`/api/vouchers/claim/${encodeURIComponent(voucher.code)}`, {
          method: 'POST',
          body: {},
        });
        // by-code omits creditCents; the claim response returns the full
        // voucher row, so credit gifts can show their exact dollar amount.
        if (res.voucher?.creditCents != null) {
          setVoucher(v => (v ? { ...v, creditCents: res.voucher!.creditCents } : v));
        }
        setAlreadyClaimed(!!res.alreadyClaimed);
        setClaimed(true);
        // Land the user in their account with the gift in hand.
        setTimeout(goToRewards, 2500);
      } catch (e: any) {
        const msg = e instanceof ApiError ? (e.message || 'Could not claim this gift') : 'Could not claim this gift';
        setClaimError(msg);
      } finally {
        setClaiming(false);
      }
    })();
  }, [authLoading, isAuthenticated, voucher, claimed, claiming]);

  if (loading || authLoading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error || !voucher) {
    return (
      <>
        <Stack.Screen options={{ title: 'Redeem Gift' }} />
        <View style={styles.container}>
          <ContentContainer maxWidth={480} style={{ alignItems: 'center' }}>
            <Ionicons name="alert-circle-outline" size={80} color={colors.danger} />
            <Text style={styles.title}>Gift not found</Text>
            <Text style={styles.subtitle}>{error ?? 'The code you used doesn’t match any active gift.'}</Text>
            <Button title="Go to RinsePay" onPress={() => router.replace('/')} style={styles.btn} />
          </ContentContainer>
        </View>
      </>
    );
  }

  const terminal = statusBanner(voucher.status);
  const expiresLine = voucher.expiresAt
    ? `Expires ${new Date(voucher.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : null;

  return (
    <>
      <Stack.Screen options={{ title: 'Your Gift' }} />
      <ScrollView style={{ backgroundColor: colors.background }} contentContainerStyle={styles.scroll}>
        <ContentContainer maxWidth={480}>

          <View style={styles.card}>
            <Ionicons name="gift" size={56} color={colors.primary} style={{ alignSelf: 'center' }} />
            <Text style={styles.headline}>
              {voucher.discountKind === 'free_vend' || voucher.discountKind === 'credit' ? 'You’ve got a gift' : 'You’ve got a gift wash'}
            </Text>
            <Text style={styles.subhead}>
              from <Text style={{ fontWeight: '700' as any }}>{voucher.ownerName}</Text>
              {voucher.locationName ? ` at ${voucher.locationName}` : ''}
            </Text>

            <View style={styles.offerBox}>
              <Text style={styles.offerHeadline}>{offerHeadline(voucher)}</Text>
              {voucher.appliesToLabel && <Text style={styles.offerMeta}>Valid on: {voucher.appliesToLabel}</Text>}
              {expiresLine && <Text style={styles.offerMeta}>{expiresLine}</Text>}
            </View>

            {voucher.message ? (
              <View style={styles.messageBox}>
                <Text style={styles.messageText}>“{voucher.message}”</Text>
              </View>
            ) : null}

            {terminal ? (
              <View style={[styles.banner, styles.bannerErr]}>
                <Text style={styles.bannerText}>{terminal.text}</Text>
              </View>
            ) : !isAuthenticated ? (
              <View style={{ marginTop: spacing.lg }}>
                {voucher.status === 'redeemed' && (
                  <View style={[styles.banner, { backgroundColor: '#fef3c7' }]}>
                    <Ionicons name="information-circle" size={20} color="#a16207" />
                    <Text style={[styles.bannerText, { color: '#92400e' }]}>
                      All claims for this code have been taken — if you already claimed it, sign in to use your gift
                    </Text>
                  </View>
                )}
                <Text style={styles.helper}>
                  Sign in to claim your gift. New here? Create a free account in under a minute.
                </Text>
                <Button
                  title="Sign In"
                  onPress={() => router.push(`/login?returnTo=${encodeURIComponent(`/redeem/${voucher.code}`)}`)}
                  style={styles.btn}
                />
                <Button
                  title="Create Account"
                  variant="secondary"
                  onPress={() => router.push(`/register?returnTo=${encodeURIComponent(`/redeem/${voucher.code}`)}`)}
                  style={styles.btn}
                />
              </View>
            ) : claiming ? (
              <View style={{ marginTop: spacing.lg, alignItems: 'center' }}>
                <ActivityIndicator color={colors.primary} />
                <Text style={[styles.helper, { marginTop: spacing.sm }]}>Claiming your gift…</Text>
              </View>
            ) : claimed ? (
              <View style={{ marginTop: spacing.lg }}>
                <View style={[styles.banner, styles.bannerOk]}>
                  <Ionicons name="checkmark-circle" size={20} color="#16a34a" />
                  <Text style={[styles.bannerText, { color: '#15803d' }]}>
                    {voucher.discountKind === 'credit'
                      ? (alreadyClaimed
                        ? 'This credit is already in your balance'
                        : `${voucher.creditCents != null ? `$${(voucher.creditCents / 100).toFixed(2)} of ` : ''}gift credit was added to your balance`)
                      : alreadyClaimed ? 'This gift is in your account' : 'Gift added to your account'}
                  </Text>
                </View>
                <Text style={styles.helper}>
                  {voucher.discountKind === 'credit'
                    ? 'Taking you to your rewards… Your credit applies automatically the next time you pay — nothing else to redeem.'
                    : 'Taking you to your rewards… Pull up to a wash bay and scan its QR code — your gift will appear on the payment screen.'}
                </Text>
                <Button title="View My Rewards" onPress={goToRewards} style={styles.btn} />
              </View>
            ) : claimError ? (
              <View style={{ marginTop: spacing.lg }}>
                <View style={[styles.banner, styles.bannerErr]}>
                  <Text style={styles.bannerText}>{claimError}</Text>
                </View>
                <Button title="Go to RinsePay" onPress={() => router.replace(homePath as never)} style={styles.btn} />
              </View>
            ) : null}
          </View>
        </ContentContainer>
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, alignItems: 'center', justifyContent: 'center', padding: spacing.lg },
  scroll: { paddingVertical: spacing.xl, alignItems: 'center', flexGrow: 1, justifyContent: 'center' },
  card: {
    backgroundColor: c.surface,
    borderRadius: 16,
    padding: spacing.xl,
    width: '100%',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  headline: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold as any,
    color: c.text,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  subhead: {
    fontSize: fontSize.md,
    color: c.textSecondary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  offerBox: {
    backgroundColor: c.background,
    borderColor: c.primary,
    borderWidth: 2,
    borderRadius: 12,
    padding: spacing.lg,
    marginTop: spacing.lg,
    alignItems: 'center',
  },
  offerHeadline: {
    fontSize: fontSize.lg,
    fontWeight: fontWeight.bold as any,
    color: c.text,
  },
  offerMeta: {
    fontSize: fontSize.sm,
    color: c.textSecondary,
    marginTop: spacing.xs,
  },
  messageBox: {
    backgroundColor: c.background,
    borderLeftColor: c.primary,
    borderLeftWidth: 3,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 6,
    marginTop: spacing.md,
  },
  messageText: { color: c.textSecondary, fontStyle: 'italic', fontSize: fontSize.sm, lineHeight: 20 },
  helper: { color: c.textSecondary, fontSize: fontSize.sm, textAlign: 'center', marginTop: spacing.sm, marginBottom: spacing.md, lineHeight: 20 },
  banner: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: 8, marginVertical: spacing.sm },
  bannerOk: { backgroundColor: '#dcfce7' },
  bannerErr: { backgroundColor: '#fee2e2' },
  bannerText: { fontSize: fontSize.sm, color: '#991b1b', fontWeight: fontWeight.semibold as any },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold as any, color: c.text, marginTop: spacing.lg, textAlign: 'center' },
  subtitle: { fontSize: fontSize.md, color: c.textSecondary, marginTop: spacing.sm, textAlign: 'center' },
  btn: { marginTop: spacing.sm, width: '100%' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
